import { useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { SessionTimeoutWarning } from '@/components/SessionTimeoutWarning'
import { PortalPjBreadcrumbs, PortalPjSupertopo } from '@/components/PortalPj/PortalPjSupertopo'
import { PortalPjTabsNav } from '@/components/PortalPj/PortalPjTabsNav'
import { useSessionTimeout } from '@/hooks/useSessionTimeout'
import { useBehavioralProtection } from '@/hooks/useBehavioralProtection'
import { isPjRole } from '@/config/routeAccess'
import { useAuthStore } from '@/stores/authStore'

export type PortalPjTab = 'nova' | 'meus' | 'nf' | 'feedback'

interface PortalPjLayoutProps {
  children: React.ReactNode
  activeTab: PortalPjTab
  onTabChange: (tab: PortalPjTab) => void
  crmHomeHref?: string
}

export function PortalPjLayout({
  children,
  activeTab,
  onTabChange,
  crmHomeHref,
}: PortalPjLayoutProps) {
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const mainRef = useRef<HTMLElement>(null)

  useBehavioralProtection(Boolean(user && isPjRole(user.role)), 'enforce')

  const rememberEmail =
    typeof window !== 'undefined' &&
    Boolean(localStorage.getItem('crm_secure_remember_email'))

  const handleSessionLogout = useCallback(() => {
    void logout().finally(() => {
      navigate('/login?reason=inactivity', { replace: true })
    })
  }, [logout, navigate])

  const sessionTimeout = useSessionTimeout(handleSessionLogout, !rememberEmail)

  const handleLogout = () => {
    void logout().finally(() => {
      navigate('/login', { replace: true })
    })
  }

  const focusSearch = () => {
    onTabChange('nova')
    requestAnimationFrame(() => {
      document.getElementById('portal-pj-search')?.focus()
    })
  }

  return (
    <div className='portal-pj-page flex min-h-screen flex-col bg-[#0d0221] font-sans text-white'>
      <PortalPjSupertopo
        userName={user?.name}
        onLogout={handleLogout}
        onSearchClick={focusSearch}
        crmHomeHref={crmHomeHref}
      />

      <main ref={mainRef} className='mx-auto w-full max-w-4xl flex-1 px-4 pb-10 pt-6 sm:px-6 sm:pt-8'>
        <PortalPjBreadcrumbs className='mb-5 text-white/60' crmHomeHref={crmHomeHref} />
        <PortalPjTabsNav activeTab={activeTab} onTabChange={onTabChange} showFeedback={Boolean(user && isPjRole(user.role))} />
        {children}
      </main>

      <footer className='border-t border-white/10 bg-[#080116] py-4 text-center text-xs text-white/50'>
        Portal de solicitações · RocketSearch
      </footer>

      <SessionTimeoutWarning
        show={sessionTimeout.showWarning}
        timeLeft={sessionTimeout.timeLeft}
        onExtendSession={sessionTimeout.extendSession}
        onLogout={sessionTimeout.forceLogout}
      />
    </div>
  )
}
